/**
 * CONSÓRCIO BAZILIO — data layer das 6 verticais.
 * Fonte única para hero, benefícios, faixas de crédito, FAQ, SEO por vertical.
 */
import { ASSETS } from "@/lib/assets";

export type VerticalKey =
  | "veiculos"
  | "motos"
  | "imoveis"
  | "agro"
  | "pesados"
  | "servicos";

export interface VerticalBenefit {
  icon: "check" | "shield" | "trophy" | "sparkle" | "wallet" | "target";
  title: string;
  description: string;
}

export interface FaixaCredito {
  range: string;
  parcelaFrom: string;
  prazoMeses: number;
}

export interface VerticalFaqItem {
  q: string;
  a: string;
}

/** Badge de confiança exibido no hero (paleta preto+dourado). */
export interface HeroBadge {
  icon: "shield" | "check" | "trophy";
  label: string;
}

export interface VerticalConfig {
  key: VerticalKey;
  routePath: string;
  navLabel: string;
  seo: {
    title: string;
    description: string;
    ogImage?: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    highlight: string;
    subtitle: string;
    /** Foto de fundo do hero (metade direita desktop / full mobile sob overlay preto). */
    bgImage?: string;
    /** Tom do hero — paleta fiel às logos (preto + dourado). `noir` (preto puro) ou
     *  `goldNoir` (preto → bronze quente). Ambos com texto branco + acento dourado. */
    tone: "noir" | "goldNoir";
  };
  /** Substitui os 3 selos padrão do hero quando presente. */
  heroBadges?: HeroBadge[];
  /** Fixa o consultor da página (slug em personas.ts). Default = getPrimaryPersonaFor(key). */
  specialistSlug?: string;
  benefits: VerticalBenefit[];
  faixas: FaixaCredito[];
  faq: VerticalFaqItem[];
  ctaWhatsAppMessage: string;
}

export const VERTICAIS: Record<VerticalKey, VerticalConfig> = {
  veiculos: {
    key: "veiculos",
    routePath: "/consorcio/veiculos",
    navLabel: "Veículos",
    seo: {
      title: "Consórcio de Veículos | Consórcio Bazilio",
      description:
        "Cartas de crédito de R$ 30 mil a R$ 200 mil para carros 0 km e seminovos. Sem juros, sem entrada e com parcelas em até 120 meses. Atendimento consultivo da equipe Bazilio.",
    },
    hero: {
      eyebrow: "Consórcio de Veículos",
      title: "Seu próximo carro,",
      highlight: "sem juros e sem entrada.",
      subtitle:
        "Cartas de crédito de R$ 30 mil a R$ 200 mil. Parcelas que cabem no bolso e prazos de até 120 meses. Você escolhe o modelo e a equipe Bazilio ajuda na simulação.",
      tone: "noir",
      bgImage: ASSETS.r2.vehiclesBR.compass,
    },
    benefits: [
      {
        icon: "wallet",
        title: "Parcela até 5x menor",
        description:
          "Comparado ao financiamento tradicional. Você guarda o que ia pagar de juros.",
      },
      {
        icon: "shield",
        title: "Administradoras Bacen",
        description:
          "Trabalhamos apenas com administradoras autorizadas pelo Banco Central.",
      },
      {
        icon: "target",
        title: "Contemplação estratégica",
        description:
          "A equipe Bazilio ajuda você a montar o melhor lance para acelerar a contemplação.",
      },
      {
        icon: "sparkle",
        title: "Pós-venda completo",
        description:
          "Atendimento consultivo pelo WhatsApp do início ao fim, com acompanhamento de verdade.",
      },
    ],
    faixas: [
      { range: "R$ 30 mil a R$ 50 mil", parcelaFrom: "R$ 289", prazoMeses: 80 },
      { range: "R$ 50 mil a R$ 80 mil", parcelaFrom: "R$ 458", prazoMeses: 100 },
      { range: "R$ 80 mil a R$ 120 mil", parcelaFrom: "R$ 689", prazoMeses: 100 },
      { range: "R$ 120 mil a R$ 200 mil", parcelaFrom: "R$ 989", prazoMeses: 120 },
    ],
    faq: [
      {
        q: "Posso comprar carro 0km e seminovo pelo consórcio?",
        a: "Sim. A carta de crédito é usada como se fosse à vista. Você negocia desconto na concessionária ou com o vendedor particular.",
      },
      {
        q: "Preciso dar entrada?",
        a: "Não. No Consórcio Bazilio você não paga entrada. As parcelas começam já no primeiro mês.",
      },
      {
        q: "Quanto tempo até ser contemplado?",
        a: "Depende da sua estratégia. Sorteios mensais dão contemplação a partir do 1º mês. Com lance embutido ou FGTS a chance é ainda maior.",
      },
      {
        q: "Posso usar o carro atual como lance?",
        a: "Sim. Seu seminovo pode ser usado como lance, sujeito à análise da administradora.",
      },
      {
        q: "E se eu não puder pagar as parcelas?",
        a: "Existem opções: transferência de cota, redução de parcela, uso da reserva técnica. Nunca fique inadimplente sem falar com a gente antes.",
      },
    ],
    ctaWhatsAppMessage:
      "Olá! Vim pelo site do Consórcio Bazilio e quero simular meu consórcio de veículos.",
  },
  motos: {
    key: "motos",
    routePath: "/consorcio/motos",
    navLabel: "Motos",
    seo: {
      title: "Consórcio de Motos | Consórcio Bazilio",
      description:
        "Cartas de crédito de R$ 8 mil a R$ 60 mil para motos 0 km. Sem juros, sem entrada e com parcelas leves.",
    },
    hero: {
      eyebrow: "Consórcio de Motos",
      title: "Sua moto nova,",
      highlight: "sem sofrer com juros.",
      subtitle:
        "De 125cc a big trail, com cartas de R$ 8 mil a R$ 60 mil. Parcelas leves, prazos flexíveis e atendimento rápido da equipe Bazilio.",
      tone: "goldNoir",
      bgImage: ASSETS.r2.dreams.moto,
    },
    heroBadges: [
      { icon: "shield", label: "Administradoras Bacen" },
      { icon: "check", label: "Sem juros, sem entrada" },
      { icon: "trophy", label: "Contemplação rápida" },
    ],
    benefits: [
      {
        icon: "wallet",
        title: "Parcela cabe no combustível",
        description:
          "Mais barato que financiamento. Muitas vezes cabe até no valor do tanque cheio.",
      },
      {
        icon: "target",
        title: "Contemplação rápida",
        description:
          "Grupos de moto costumam girar mais rápido, com chances mensais de contemplação.",
      },
      {
        icon: "shield",
        title: "Cobertura total",
        description:
          "Administradora autorizada Bacen. Sua cota é protegida por lei.",
      },
      {
        icon: "sparkle",
        title: "Processo simples",
        description:
          "Você envia os dados básicos e nossa equipe cuida da simulação.",
      },
    ],
    faixas: [
      { range: "R$ 8 mil a R$ 15 mil", parcelaFrom: "R$ 148", prazoMeses: 60 },
      { range: "R$ 15 mil a R$ 25 mil", parcelaFrom: "R$ 218", prazoMeses: 70 },
      { range: "R$ 25 mil a R$ 40 mil", parcelaFrom: "R$ 338", prazoMeses: 80 },
      { range: "R$ 40 mil a R$ 60 mil", parcelaFrom: "R$ 489", prazoMeses: 80 },
    ],
    faq: [
      {
        q: "Serve pra qualquer modelo de moto?",
        a: "Sim, desde 125cc populares até esportivas de alta cilindrada. Você escolhe o modelo depois de contemplado.",
      },
      {
        q: "Posso comprar moto usada?",
        a: "Sim, desde que tenha até 10 anos de uso. A equipe Bazilio confirma com você a regra da administradora escolhida.",
      },
      {
        q: "Qual o valor mínimo da parcela?",
        a: "A partir de R$ 148 por mês na carta de R$ 8 mil, conforme o prazo escolhido.",
      },
      {
        q: "Tem sorteio ou é só por lance?",
        a: "Todo mês tem sorteio E ofertas de lance. Você concorre nas duas modalidades sem custo extra.",
      },
    ],
    ctaWhatsAppMessage:
      "Olá! Vim pelo site do Consórcio Bazilio e quero simular meu consórcio de moto.",
  },
  imoveis: {
    key: "imoveis",
    routePath: "/consorcio/imoveis",
    navLabel: "Imóveis",
    seo: {
      title: "Consórcio de Imóveis | Consórcio Bazilio",
      description:
        "Crédito imobiliário de R$ 100 mil a R$ 1,5 milhão sem juros, sem entrada. Casa, apartamento, terreno, construção ou reforma. Use FGTS como lance.",
    },
    hero: {
      eyebrow: "Consórcio de Imóveis",
      title: "Seu imóvel próprio,",
      highlight: "sem juros de financiamento.",
      subtitle:
        "Cartas de R$ 100 mil a R$ 1,5 milhão. Casa, apartamento, terreno, reforma ou construção. Use FGTS como lance e acelere a conquista.",
      tone: "noir",
      bgImage: ASSETS.r2.propertiesPremium.aptoVista,
    },
    heroBadges: [
      { icon: "trophy", label: "FGTS como lance" },
      { icon: "check", label: "Sem juros, sem entrada" },
      { icon: "shield", label: "Correção só pelo INCC" },
    ],
    benefits: [
      {
        icon: "wallet",
        title: "Você economiza uma casa",
        description:
          "Um financiamento imobiliário paga 2x o valor do imóvel em juros. No consórcio, você paga só o bem.",
      },
      {
        icon: "trophy",
        title: "Use FGTS como lance",
        description:
          "O saldo do FGTS pode virar lance forte e antecipar sua contemplação em anos.",
      },
      {
        icon: "shield",
        title: "Correção só pelo INCC",
        description:
          "Enquanto financiamento sobe pela SELIC, seu consórcio é corrigido só pela inflação da construção.",
      },
      {
        icon: "target",
        title: "Análise no momento certo",
        description:
          "A análise de crédito acontece na contemplação. Até lá, você ganha tempo para se organizar.",
      },
    ],
    faixas: [
      { range: "R$ 100 mil a R$ 250 mil", parcelaFrom: "R$ 689", prazoMeses: 180 },
      { range: "R$ 250 mil a R$ 500 mil", parcelaFrom: "R$ 1.389", prazoMeses: 200 },
      { range: "R$ 500 mil a R$ 800 mil", parcelaFrom: "R$ 2.189", prazoMeses: 220 },
      { range: "R$ 800 mil a R$ 1,5 mi", parcelaFrom: "R$ 3.789", prazoMeses: 240 },
    ],
    faq: [
      {
        q: "Serve pra comprar terreno e construir?",
        a: "Sim. A carta pode ser usada para terreno e construção, com orientação da equipe Bazilio.",
      },
      {
        q: "Posso usar meu FGTS?",
        a: "Sim. FGTS pode ser usado como lance, para completar o valor do imóvel ou para amortizar parcelas depois da contemplação.",
      },
      {
        q: "Aceita imóvel usado?",
        a: "Sim. Comercial, residencial, urbano ou rural.",
      },
      {
        q: "Quanto tempo até ter meu imóvel?",
        a: "Depende da estratégia de lance. Com FGTS e lance bem montado, muita gente antecipa a contemplação.",
      },
      {
        q: "Tenho nome sujo, posso participar?",
        a: "Pode participar. A análise de crédito acontece na contemplação, não na entrada do grupo.",
      },
    ],
    ctaWhatsAppMessage:
      "Olá! Vim pelo site do Consórcio Bazilio e quero simular meu consórcio de imóvel.",
  },
  agro: {
    key: "agro",
    routePath: "/consorcio/agro",
    navLabel: "Agronegócio",
    seo: {
      title: "Consórcio Agro | Consórcio Bazilio",
      description:
        "Crédito rural até R$ 1,2 milhão para máquinas, terras, insumos, gado e benfeitorias. Sem juros, sem entrada. Preserve seu capital de giro.",
    },
    hero: {
      eyebrow: "Consórcio Agro",
      title: "Cresça no campo",
      highlight: "sem comprometer seu capital de giro.",
      subtitle:
        "Máquinas, terras, gado, insumos, benfeitorias. Cartas de R$ 50 mil a R$ 1,2 milhão. Prazos que respeitam o ciclo do produtor.",
      tone: "noir",
      bgImage: ASSETS.r2.agroBR.fazenda,
    },
    heroBadges: [
      { icon: "check", label: "Capital de giro preservado" },
      { icon: "shield", label: "Administradoras Bacen" },
      { icon: "trophy", label: "Prazos sazonais" },
    ],
    benefits: [
      {
        icon: "wallet",
        title: "Capital de giro preservado",
        description:
          "Não trave sua safra pagando entrada. Comece a parcelar e mantenha caixa pro plantio.",
      },
      {
        icon: "trophy",
        title: "Cartas altas",
        description:
          "Até R$ 1,2 mi, suficiente para trator novo, colheitadeira ou hectares de terra.",
      },
      {
        icon: "shield",
        title: "Sem juros bancários",
        description:
          "Em comparação com linhas tradicionais, o consórcio reduz o custo total do investimento.",
      },
      {
        icon: "target",
        title: "Prazos flexíveis",
        description:
          "Até 200 meses. Parcelas ajustáveis ao seu fluxo sazonal.",
      },
    ],
    faixas: [
      { range: "R$ 50 mil a R$ 150 mil", parcelaFrom: "R$ 419", prazoMeses: 140 },
      { range: "R$ 150 mil a R$ 400 mil", parcelaFrom: "R$ 989", prazoMeses: 180 },
      { range: "R$ 400 mil a R$ 800 mil", parcelaFrom: "R$ 1.889", prazoMeses: 200 },
      { range: "R$ 800 mil a R$ 1,2 mi", parcelaFrom: "R$ 2.789", prazoMeses: 200 },
    ],
    faq: [
      {
        q: "Serve pra que tipo de investimento agro?",
        a: "Máquinas (trator, colheitadeira, pulverizador), terras (rural ou compra de posse), gado (bezerros, matrizes), benfeitorias (silo, cerca, galpão) e insumos.",
      },
      {
        q: "Sou pequeno produtor. Posso participar?",
        a: "Sim. Fale com a equipe Bazilio para encontrar a faixa mais adequada ao seu porte.",
      },
      {
        q: "Como funciona o pagamento da parcela na entressafra?",
        a: "Administradoras aceitam parcelas semestrais ou anuais em alguns grupos. Também é possível usar carência inicial.",
      },
      {
        q: "Preciso de garantia real?",
        a: "Não na entrada. A garantia é constituída após a contemplação, geralmente com o próprio bem adquirido.",
      },
    ],
    ctaWhatsAppMessage:
      "Olá! Vim pelo site do Consórcio Bazilio e quero simular meu consórcio agro.",
  },
  pesados: {
    key: "pesados",
    routePath: "/consorcio/pesados",
    navLabel: "Pesados",
    seo: {
      title: "Consórcio de Caminhões e Pesados | Consórcio Bazilio",
      description:
        "Cartas de R$ 100 mil a R$ 800 mil para caminhões, ônibus, cavalos mecânicos e implementos. Sem juros, sem entrada. Frota nova sem travar caixa.",
    },
    hero: {
      eyebrow: "Consórcio de Pesados",
      title: "Frota renovada,",
      highlight: "sem travar o caixa da empresa.",
      subtitle:
        "Caminhões, ônibus, cavalos mecânicos, implementos rodoviários e utilitários. Cartas de R$ 100 mil a R$ 800 mil, com prazo de até 200 meses.",
      tone: "noir",
      bgImage: ASSETS.r2.vehiclesBR.hilux,
    },
    heroBadges: [
      { icon: "shield", label: "PJ e CNPJ aceitos" },
      { icon: "check", label: "Sem juros, sem entrada" },
      { icon: "trophy", label: "Renove sua frota" },
    ],
    benefits: [
      {
        icon: "wallet",
        title: "Custo total até 40% menor",
        description:
          "Comparado ao FINAME e leasing, o consórcio de pesados corta juros e IOF.",
      },
      {
        icon: "trophy",
        title: "Renove sua frota",
        description:
          "Cartas encadeadas permitem substituir 2, 3 ou 5 caminhões em ciclo previsível.",
      },
      {
        icon: "shield",
        title: "Administradoras robustas",
        description:
          "Grupos específicos para pesados, pensados para quem precisa planejar a renovação da frota.",
      },
      {
        icon: "target",
        title: "Lance com veículo usado",
        description:
          "Seu caminhão antigo pode virar lance e antecipar a chegada da frota nova.",
      },
    ],
    faixas: [
      { range: "R$ 100 mil a R$ 250 mil", parcelaFrom: "R$ 689", prazoMeses: 140 },
      { range: "R$ 250 mil a R$ 400 mil", parcelaFrom: "R$ 1.189", prazoMeses: 160 },
      { range: "R$ 400 mil a R$ 600 mil", parcelaFrom: "R$ 1.689", prazoMeses: 180 },
      { range: "R$ 600 mil a R$ 800 mil", parcelaFrom: "R$ 2.189", prazoMeses: 200 },
    ],
    faq: [
      {
        q: "Serve pra caminhão zero e usado?",
        a: "Ambos. Seminovo é aceito. A carta funciona como pagamento à vista na concessionária ou vendedor.",
      },
      {
        q: "Posso comprar ônibus rodoviário?",
        a: "Sim. E também caminhão baú, tanque, betoneira, implementos e utilitários pesados.",
      },
      {
        q: "MEI ou microempresário consegue contratar?",
        a: "Sim. Pessoa física ou jurídica, MEI, transportador e produtor rural podem participar, conforme análise da administradora.",
      },
      {
        q: "Trabalho na estrada. Como assino contrato?",
        a: "Contrato 100% digital via WhatsApp e link seguro. Você assina de onde estiver.",
      },
    ],
    ctaWhatsAppMessage:
      "Olá! Vim pelo site do Consórcio Bazilio e quero simular meu consórcio de pesados.",
  },
  servicos: {
    key: "servicos",
    routePath: "/consorcio/servicos",
    navLabel: "Serviços",
    seo: {
      title: "Consórcio de Serviços | Consórcio Bazilio",
      description:
        "Reforma, viagem, faculdade, casamento, procedimentos estéticos, cirurgias. Cartas de R$ 3 mil a R$ 80 mil. Sem juros, sem entrada.",
    },
    hero: {
      eyebrow: "Consórcio de Serviços",
      title: "Planeje o serviço que você quer contratar,",
      highlight: "com parcelas sem juros.",
      subtitle:
        "Reforma, viagem, faculdade, casamento, estética, cirurgia e capacitação. Cartas de R$ 3 mil a R$ 80 mil para tirar o plano do papel com organização.",
      tone: "goldNoir",
      bgImage: ASSETS.r2.dreams.viagem,
    },
    heroBadges: [
      { icon: "check", label: "Sem cartão, sem juros" },
      { icon: "trophy", label: "Contemplação em 4-8 meses" },
      { icon: "shield", label: "Regulado pelo Bacen" },
    ],
    benefits: [
      {
        icon: "wallet",
        title: "Sem cartão, sem juros",
        description:
          "Uma alternativa ao parcelamento no cartão e aos juros das linhas tradicionais.",
      },
      {
        icon: "sparkle",
        title: "Uso flexível",
        description:
          "Em muitos grupos, a carta é liberada na contemplação para pagamento do serviço conforme as regras da administradora.",
      },
      {
        icon: "target",
        title: "Grupos ágeis",
        description:
          "Cartas menores costumam contemplar mais rápido e permitem lances mais leves.",
      },
      {
        icon: "shield",
        title: "Segurança regulada",
        description:
          "Bacen fiscaliza. Sua cota é protegida por lei mesmo se sair do grupo.",
      },
    ],
    faixas: [
      { range: "R$ 3 mil a R$ 8 mil", parcelaFrom: "R$ 58", prazoMeses: 60 },
      { range: "R$ 8 mil a R$ 20 mil", parcelaFrom: "R$ 149", prazoMeses: 70 },
      { range: "R$ 20 mil a R$ 40 mil", parcelaFrom: "R$ 289", prazoMeses: 80 },
      { range: "R$ 40 mil a R$ 80 mil", parcelaFrom: "R$ 549", prazoMeses: 80 },
    ],
    faq: [
      {
        q: "Para quais serviços posso usar a carta?",
        a: "Livre. Reforma, viagem, faculdade, pós-graduação, casamento, cirurgia plástica, tratamento odontológico, capacitação profissional, aparelhos de saúde.",
      },
      {
        q: "Preciso comprovar o uso?",
        a: "Depende da administradora. Há grupos em que o pagamento é liberado conforme a documentação do serviço.",
      },
      {
        q: "Qual carta é a mais popular?",
        a: "R$ 15 mil a R$ 30 mil. Essa faixa costuma atender reforma média, viagem, faculdade e eventos.",
      },
      {
        q: "Consigo cotas parceladas em 40x?",
        a: "Sim. Grupos de serviços têm prazos de 40 a 80 meses. Parcela ajustada ao seu bolso.",
      },
    ],
    ctaWhatsAppMessage:
      "Olá! Vim pelo site do Consórcio Bazilio e quero simular meu consórcio de serviços.",
  },
};

export const VERTICAIS_LIST: VerticalConfig[] = Object.values(VERTICAIS);
